<template>
  <div
    v-if="displayed"
    :id="`lead-card-${state}-${deal.id}`"
    class="lead"
    :class="{ 'lock': locked }"
    :data-deal-id="deal.id"
    :data-orga-name="deal.buyer_organization.name"
    :data-current-state="currentState"
    :data-current-column="state"
  >
    <div class="lead__header lh">
      <div class="lh__interlocutor lmi">
        <Profile
          v-if="isProspect"
          :user-fullname="trans('user.prospect_name')"
        />
        <Profile
          v-else
          :user="interlocutor"
          :user-firstname="interlocutorShortName"
          :user-fullname="interlocutorFullName"
        />
      </div>
      <dots-dropdown :dropdown-id="`deal-${deal.id}`">
        <template #list-actions>
          <a
            v-show="meta.last_message !== ''"
            :href="dealMessage()"
            :title="
              deal.meta_user_has_read === false
                ? trans('user.inbox.messages')
                : trans('user.inbox.no_messages.title')
            "
          >
            {{ trans('lead.table.card.messages') }}
          </a>
          <a :href="meta.meta_offer_url" target="_blank" rel="noopener">
            {{ trans('offer.detail') }}
          </a>
          <a
            v-if="isVisitCancelable"
            :href="cancelVisitUrl"
            target="_blank"
          >
            {{ trans('deal_dashboard.card.actions.cancel_visit') }}
          </a>
        </template>
        <template v-if="!isArchived" #list-footer>
          <a
            v-show="meta.meta_is_author_or_buyer"
            :title="trans('deal.archive.action')"
            @click="archive"
          >
            {{ trans('deal.archive.action') }}
          </a>
        </template>
      </dots-dropdown>
    </div>
    <div class="lead__infos lead-infos">
      <div v-if="isMultiBureau" class="lead-infos__title">
        {{ trans('inbox.combinaison.title') }}
      </div>
      <DealOffersNameList :deal="deal" />
      <div class="lead-infos__icon lead-infos__icon--price">
        {{ currency(deal.amount) }} {{ trans('global.price_tax') }} /
        {{ trans('global.duration.month') }}
      </div>
      <div
        v-if="isArchived"
        class="labels__label"
      >
        <div class="labels__label-text">
          {{ trans('lead.table.card.archived') }}
        </div>
      </div>
    </div>
    <div v-if="isProspect" class="lead-actions__accept">
      <a :href="dealMessage()" class="btn btn--primary">{{ trans('lead.table.card.accept') }}</a>
    </div>
    <div v-else class="lead-actions__move">
      <ExtendableButton
        :id="`lead-card-deal-${deal.id}`"
        :text="trans('lead.table.card.action.move')"
        :is-active-prop="moveButtonIsActive"
        :on-toggle="onToggle"
        :on-choose="onChoose"
        :list="listOfStatesForMovingButton"
        :as-link="false"
      />
    </div>
  </div>
</template>

<script>
import { storeToRefs } from 'pinia';
import DealOffersNameList from '@components/DealOffersNameList.vue';
import ExtendableButton from '@components/ExtendableButton.vue';
import { useScreensizeStore } from '@stores/useScreensizeStore.ts';
import DotsDropdown from '@components-molecules/DotsDropdown.vue';
import Profile from '@components-molecules/Profile.vue';
import { currency } from '@elements/number-formatter';
import { isDateInFuture, setToEndOfDay } from '@tools/date';
import eventBus from '@tools/event-bus';
import { URL_DEAL_MESSAGING } from '@tools/routes/routes';
import translation from '@elements/translation';
import dealMixin from '@organisms/dealMixin';
import { COLUMN__VISITED, EVENTS } from '../deal-dashboard/constants';

export default {
  components: { DealOffersNameList, Profile, ExtendableButton, DotsDropdown },
  mixins: [ translation, dealMixin ],
  props: {
    states: {
      type: Array,
      default: () => {
      },
    },
    deal: {
      type: Object,
      default: () => {
      },
    },
    meta: {
      type: Object,
      default: () => {
      },
    },
    displayArchived: { type: Boolean, default: false },
    state: { type: String, default: '' },
    index: { type: Number, default: -1 },
  },
  setup() {
    const { isMobileOrTablet } = storeToRefs(useScreensizeStore());

    return {
      isMobileOrTablet,
    };
  },
  data() {
    return {
      actionsDisplayed: false,
      moveButtonIsActive: false,
      displayed: true,
      locked: false,
      isArchived: this.deal.archived_by_seller,
    };
  },
  computed: {
    currentState() {
      return this.deal.state;
    },
    cancelVisitUrl() {
      return this.inboxMessageUrl.concat('?cancel-visit=1');
    },
    inboxMessageUrl() {
      return URL_DEAL_MESSAGING.replace('{id}', this.deal.id);
    },
    interlocutor() {
      return this.deal.buyer;
    },
    interlocutorShortName() {
      return this.interlocutor.firstname !== '' ? this.interlocutor.firstname : this.interlocutor.lastname;
    },
    interlocutorFullName() {
      return [ this.interlocutor.firstname, this.interlocutor.lastname ].filter((el) => el).join(' ');
    },
    isProspect() {
      return this.currentState === 'contacted';
    },
    isVisitCancelable() {
      return !this.deal.archived_by_buyer
        && this.isVisitShown
        && isDateInFuture(this.deal.visited_date.iso, setToEndOfDay());
    },
    isVisitShown() {
      return this.currentState === COLUMN__VISITED && this.deal.visited_date !== null;
    },
    /**
     * Transform ['in_progress', 'state2', ..]
     * into [{state:'in_progress', trans:'En cours'}, {...}]
     */
    listOfStatesForMovingButton() {
      const states = this.states.filter(
        (state) => state !== this.currentState
          && !(this.currentState === 'contacted' && state === 'in_progress'),
      );

      return states.map((state) => ({
        state,
        trans: this.trans(`lead.table.card.action.move.${state}`),
      }));
    },
  },
  watch: {},
  mounted() {
    if (this.isMobileOrTablet) {
      eventBus.addEventListener('extendable-button-opened', (event) => {
        const { id } = event.detail;
        if (id !== `lead-card-deal-${this.deal.id}`) {
          this.moveButtonIsActive = false;
        }
      });
    }
  },
  methods: {
    currency,
    onChoose(state) {
      eventBus.dispatchEvent(new CustomEvent(EVENTS.CARD_MOVED, {
        detail: {
          dealId: this.deal.id,
          to: state,
          from: this.currentState,
          oldIndex: this.index,
        },
      }));
    },
    onToggle(value) {
      this.moveButtonIsActive = value;
    },
    dealMessage() {
      return URL_DEAL_MESSAGING.replace('{id}', this.deal.id);
    },
    archive() {
      this.$emit('on-archive-card', this.deal.id);
      this.locked = true;
      this.actionsDisplayed = false;
    },
    closeDropdown() {
      this.actionsDisplayed = false;
    },
  },
};
</script>
